import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "../global/functions/apiClient";
import { getAuthHeaders } from "../global/functions/authorization";

const apiUrl = process.env.REACT_APP_API_URL;

export const getCarouselMedia = createAsyncThunk(
  "carousel/getCarouselMedia",
  async (restaurantId) => {
    const response = await axios.get(
      `${apiUrl}/carousel`,
      getAuthHeaders(false, restaurantId)
    );
    return response.data;
  }
);

export const getCarouselPreview = createAsyncThunk(
  "carousel/getCarouselPreview",
  async (restaurantId) => {
    const response = await axios.get(
      `${apiUrl}/carousel/stream`,
      getAuthHeaders(false, restaurantId)
    );
    return response.data;
  }
);

export const addCarouselMedia = createAsyncThunk(
  "carousel/addCarouselMedia",
  async ({ formData, restaurantId }) => {
    const response = await axios.post(
      `${apiUrl}/carousel`,
      formData,
      getAuthHeaders(true, restaurantId)
    );
    return response.data;
  }
);

export const updateCarouselOrder = createAsyncThunk(
  "carousel/updateOrder",
  async ({ items, restaurantId }) => {
    const response =await axios.put(
      `${apiUrl}/carousel/order`,
      { items },
      getAuthHeaders(false, restaurantId)
    );
    return response.data;
  }
);

export const deleteCarouselMedia = createAsyncThunk(
  "carousel/deleteCarouselMedia",
  async ({ id, restaurantId }) => {
    await axios.delete(
      `${apiUrl}/carousel/${id}`,
      getAuthHeaders(false, restaurantId)
    );
    return id;
  }
);

const carouselSlice = createSlice({
  name: "carousel",
  initialState: {
    items: [],
    status: "idle",
    error: null,
    loading: false,
    preview: null,
  },
  reducers: {
    updateStatus: (state) => {
      state.status = "idle";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getCarouselMedia.pending, (state) => {
        state.status = "loadingCarousel";
        state.loading = true;
      })
      .addCase(getCarouselMedia.fulfilled, (state, action) => {
        state.status = "fetchData";
        state.loading = false;
        state.items = action.payload;
      })
      .addCase(getCarouselMedia.rejected, (state, action) => {
        state.status = "fetchError";
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(getCarouselPreview.pending, (state) => {
        state.status = "loadingPreview";
        state.loading = true;
      })
      .addCase(getCarouselPreview.fulfilled, (state, action) => {
        state.status = "fetchPreview";
        state.loading = false;
        state.preview = action.payload;
      })
      .addCase(getCarouselPreview.rejected, (state, action) => {
        state.status = "fetchPreviewError";
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(updateCarouselOrder.pending, (state) => {
        state.status = "loading";
        state.loading = true;
      })
      .addCase(updateCarouselOrder.fulfilled, (state, action) => {
        state.status = "updateSuccess";
        state.loading = false;
        state.success = action.payload.message;
      })
      .addCase(updateCarouselOrder.rejected, (state, action) => {
        state.status = "updateError";
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(addCarouselMedia.pending, (state) => {
        state.status = "loading";
        state.loading = true;
      })
      .addCase(addCarouselMedia.fulfilled, (state, action) => {
        state.status = "addSuccess";
        state.loading = false;
        state.success = action.payload.message;
      })
      .addCase(addCarouselMedia.rejected, (state, action) => {
        state.status = "addError";
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(deleteCarouselMedia.pending, (state) => {
        state.status = "loading";
        state.loading = true;
      })
      .addCase(deleteCarouselMedia.fulfilled, (state, action) => {
        state.status = "deleteSuccess";
        state.loading = false;
        state.success = action.payload.message;
      })
      .addCase(deleteCarouselMedia.rejected, (state, action) => {
        state.status = "deleteError";
        state.loading = false;
        state.error = action.error.message;
      });
  },
});

export const { updateStatus } = carouselSlice.actions;
export const selectAllCarousels = (state) => state.carousel.items;
export const selectCarouselPreview = (state) => state.carousel.preview;
export const getCarouselsStatus = (state) => state.carousel.status;
export const getCarouselsError = (state) => state.carousel.error;
export const getCarouselsSuccess = (state) => state.carousel.success;
export const getCarouselsLoading = (state) => state.carousel.loading;
export default carouselSlice.reducer;
